import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import type { ApexOptions } from "apexcharts";
import SafeApexChart from "../../../Components/Common/SafeApexChart";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { getLoggedinSuperAdmin } from "../../../helpers/api_helper";
import { formatCents, intlLocaleFromI18n } from "../../../helpers/money";
import {
  clearSaPlatformFreeze,
  formatSaRate,
  getSaDashboardMetrics,
  getSaDashboardSeries,
  getSaHostMetrics,
  requestSaHostSample,
  SaDashboardMetrics,
  SaHostMetrics,
  SaStatsSeries,
} from "../../../helpers/super_admin_ops_helper";

/** Sparkline CPU a partir de series — SVG leve, sem ApexCharts extra. */
const CpuSparkline = ({
  values,
  width = 160,
  height = 36,
}: {
  values: number[];
  width?: number;
  height?: number;
}) => {
  if (values.length < 2) {
    return null;
  }
  const max = Math.max(100, ...values);
  const min = 0;
  const span = Math.max(1, max - min);
  const step = width / (values.length - 1);
  const points = values
    .map((v, i) => {
      const x = i * step;
      const y = height - ((v - min) / span) * (height - 4) - 2;
      return `${x},${y}`;
    })
    .join(" ");
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="d-block"
      aria-hidden
    >
      <polyline
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        points={points}
      />
    </svg>
  );
};

type KpiTone = "ok" | "warn" | "crit";

type KpiProps = {
  label: string;
  value: string;
  hint?: string;
  icon: string;
  to?: string;
  /** Tom visual (host metrics acima do limiar). */
  tone?: KpiTone;
};

const toneAvatarClass = (tone: KpiTone | undefined): string => {
  if (tone === "crit") return "bg-danger-subtle text-danger";
  if (tone === "warn") return "bg-warning-subtle text-warning";
  return "bg-primary-subtle text-primary";
};

const KpiCard = ({ label, value, hint, icon, to, tone }: KpiProps) => {
  const borderClass =
    tone === "crit"
      ? "border border-danger"
      : tone === "warn"
        ? "border border-warning"
        : "";

  const body = (
    <CardBody>
      <div className="d-flex align-items-center">
        <div className="flex-grow-1">
          <p className="text-uppercase fw-medium text-muted mb-0">{label}</p>
          <h4 className="fs-22 fw-semibold mb-0 mt-2">{value}</h4>
          {hint ? <p className="text-muted small mb-0 mt-1">{hint}</p> : null}
        </div>
        <div className="avatar-sm flex-shrink-0">
          <span
            className={`avatar-title ${toneAvatarClass(tone)} rounded-circle fs-3`}
          >
            <i className={icon} />
          </span>
        </div>
      </div>
    </CardBody>
  );

  if (to) {
    return (
      <Card className={`card-animate h-100 mb-0 ${borderClass}`.trim()}>
        <Link to={to} className="text-decoration-none text-body">
          {body}
        </Link>
      </Card>
    );
  }

  return (
    <Card className={`card-animate h-100 mb-0 ${borderClass}`.trim()}>
      {body}
    </Card>
  );
};

/**
 * Dashboard SA — KPIs reais + freeze/queue (LAC-042 · DOC 02 § 6.1).
 */
const SuperAdminDashboard = () => {
  const { t, i18n } = useTranslation();
  const session = getLoggedinSuperAdmin();
  const name = session?.user?.name || session?.user?.email || "";
  const locale = intlLocaleFromI18n(i18n.language);

  const [metrics, setMetrics] = useState<SaDashboardMetrics | null>(null);
  const [hostMetrics, setHostMetrics] = useState<SaHostMetrics | null>(null);
  const [series, setSeries] = useState<SaStatsSeries | null>(null);
  const [seriesDays, setSeriesDays] = useState<7 | 30>(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clearingFreeze, setClearingFreeze] = useState(false);
  const [requestingHostSample, setRequestingHostSample] = useState(false);
  /** Evita toast falso no 1º load; só avisa quando freeze some sem clique SA. */
  const freezeWasActiveRef = useRef(false);
  const clearingFreezeRef = useRef(false);
  /** null = ainda sem amostra; toast só na transição true→false. */
  const workerWasOkRef = useRef<boolean | null>(null);
  /** sampled_at antes do pedido — toast quando chega amostra nova (anti-viés SA/ops). */
  const hostSampleBaselineRef = useRef<string | null>(null);

  document.title = t("sa.dashboard.documentTitle");

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) {
      setLoading(true);
    }
    setError(null);
    try {
      const [res, hostRes, seriesRes] = await Promise.all([
        getSaDashboardMetrics(),
        getSaHostMetrics().catch(() => null),
        getSaDashboardSeries({ days: seriesDays }).catch(() => null),
      ]);
      if (!res?.success || !res.data) {
        setError(t("sa.dashboard.error.load"));
        setMetrics(null);
        return;
      }
      const next = res.data;
      if (
        freezeWasActiveRef.current &&
        next.platform_freeze !== true &&
        !clearingFreezeRef.current
      ) {
        toast.success(t("sa.dashboard.freezeAutoClearedToast"), {
          autoClose: 4000,
        });
      }
      freezeWasActiveRef.current = next.platform_freeze === true;
      // Worker: só alerta quando cai (KPI sozinho some no scroll).
      if (
        workerWasOkRef.current === true &&
        next.queue?.worker_ok === false
      ) {
        toast.error(t("sa.dashboard.workerDownToast"), { autoClose: 8000 });
      }
      if (next.queue && typeof next.queue.worker_ok === "boolean") {
        workerWasOkRef.current = next.queue.worker_ok;
      }
      setMetrics(next);
      if (hostRes?.success && hostRes.data) {
        setHostMetrics(hostRes.data);
      } else {
        setHostMetrics(null);
      }
      if (seriesRes?.success && seriesRes.data) {
        setSeries(seriesRes.data);
      } else {
        setSeries(null);
      }
    } catch (e: any) {
      setError(e?.message || t("sa.dashboard.error.load"));
      setMetrics(null);
    } finally {
      if (!opts?.silent) {
        setLoading(false);
      }
    }
  }, [t, seriesDays]);

  useEffect(() => {
    void load();
  }, [load]);

  // Poll contínuo da fila/worker (LAC-028 polish) — 30s sem spinner.
  useEffect(() => {
    const id = window.setInterval(() => {
      void load({ silent: true });
    }, 30_000);
    return () => window.clearInterval(id);
  }, [load]);

  // Enquanto freeze ativo, reconsulta a cada 60s (ETA / auto-clear / toast).
  useEffect(() => {
    if (metrics?.platform_freeze !== true) {
      return;
    }
    const id = window.setInterval(() => {
      void load({ silent: true });
    }, 60_000);
    return () => window.clearInterval(id);
  }, [metrics?.platform_freeze, load]);

  const handleClearFreeze = async () => {
    setClearingFreeze(true);
    clearingFreezeRef.current = true;
    try {
      const res = await clearSaPlatformFreeze();
      toast.success(
        res?.message || t("sa.dashboard.clearFreezeSuccess"),
        { autoClose: 2500 }
      );
      if (res?.data) {
        freezeWasActiveRef.current = res.data.platform_freeze === true;
        setMetrics(res.data);
      } else {
        await load();
      }
    } catch (e: any) {
      toast.error(e?.message || t("sa.dashboard.clearFreezeError"), {
        autoClose: 4000,
      });
    } finally {
      clearingFreezeRef.current = false;
      setClearingFreeze(false);
    }
  };

  // Flag no Redis — o cron no host coleta; poll até sampled_at mudar → toast.
  const handleRequestHostSample = async () => {
    setRequestingHostSample(true);
    hostSampleBaselineRef.current =
      hostMetrics?.current?.sampled_at ?? null;
    try {
      const res = await requestSaHostSample();
      toast.success(
        res?.message || t("sa.dashboard.host.requestSampleSuccess"),
        { autoClose: 2500 }
      );
      const baseline = hostSampleBaselineRef.current;
      let attempts = 0;
      const pollId = window.setInterval(() => {
        attempts += 1;
        void getSaHostMetrics()
          .then((hostRes) => {
            if (!hostRes?.success || !hostRes.data) {
              return;
            }
            setHostMetrics(hostRes.data);
            const nextAt = hostRes.data.current?.sampled_at ?? null;
            const arrived =
              nextAt != null &&
              (baseline == null || nextAt !== baseline) &&
              hostRes.data.sample_requested !== true;
            if (arrived) {
              window.clearInterval(pollId);
              toast.success(t("sa.dashboard.host.sampleArrivedToast"), {
                autoClose: 3500,
              });
            } else if (attempts >= 24) {
              // ~2 min (24 × 5s) — para de polir sem spam.
              window.clearInterval(pollId);
            }
          })
          .catch(() => undefined);
      }, 5000);
    } catch (e: any) {
      toast.error(e?.message || t("sa.dashboard.host.requestSampleError"), {
        autoClose: 4000,
      });
    } finally {
      setRequestingHostSample(false);
    }
  };

  const freezeHoldHint = useMemo(() => {
    if (!metrics || metrics.platform_freeze !== true) {
      return null;
    }
    const reason = metrics.platform_freeze_hold_reason;
    if (reason === "rates_breaching") {
      return t("sa.dashboard.freezeHoldRates");
    }
    if (reason === "aws_enforcement") {
      const status =
        metrics.ses_account?.enforcement_status ||
        (metrics.ses_account?.sending_enabled === false ? "SendingDisabled" : "—");
      return t("sa.dashboard.freezeHoldAws", { status });
    }
    if (reason === "disabled") {
      return t("sa.dashboard.freezeHoldDisabled");
    }
    if (reason === "ready") {
      return t("sa.dashboard.freezeHoldReady");
    }
    if (reason === "cooldown" && metrics.platform_freeze_auto_clear_eta_at) {
      try {
        return t("sa.dashboard.freezeHoldCooldown", {
          time: new Date(
            metrics.platform_freeze_auto_clear_eta_at
          ).toLocaleString(locale),
        });
      } catch {
        return t("sa.dashboard.freezeHoldCooldown", { time: "—" });
      }
    }
    return t("sa.dashboard.freezeActive");
  }, [metrics, t, locale]);

  const sesEnforcementWarn = useMemo(() => {
    const status = metrics?.ses_account?.enforcement_status;
    if (!status || status === "HEALTHY") {
      return null;
    }
    if (status === "SHUTDOWN") {
      return t("sa.dashboard.sesEnforcementShutdown");
    }
    if (status === "PROBATION") {
      return t("sa.dashboard.sesEnforcementProbation");
    }
    return t("sa.dashboard.sesEnforcementOther", { status });
  }, [metrics, t]);

  const hasVolume =
    (metrics?.window_7d?.sent ?? 0) > 0 || (metrics?.window_7d?.sends_count ?? 0) > 0;

  const hasSeriesVolume = useMemo(
    () =>
      (series?.series ?? []).some((p) => p.sent > 0 || p.bounced > 0),
    [series]
  );

  const seriesChartOptions = useMemo((): ApexOptions => {
    const categories = (series?.series ?? []).map((p) => {
      try {
        return new Intl.DateTimeFormat(locale, {
          day: "2-digit",
          month: "short",
        }).format(new Date(p.date));
      } catch {
        return p.date;
      }
    });
    return {
      chart: {
        type: "line",
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      stroke: { width: 2, curve: "smooth" },
      dataLabels: { enabled: false },
      colors: ["#405189", "#f06548"],
      legend: { position: "top" },
      xaxis: { categories },
      yaxis: {
        labels: {
          formatter: (v: number) => Math.round(v).toLocaleString(locale),
        },
      },
      grid: { borderColor: "#f1f1f1" },
    };
  }, [series, locale]);

  const seriesChartData = useMemo(
    () => [
      {
        name: t("dashboard.series.sent"),
        data: (series?.series ?? []).map((p) => p.sent),
      },
      {
        name: t("dashboard.series.bounced"),
        data: (series?.series ?? []).map((p) => p.bounced),
      },
    ],
    [series, t]
  );

  const formatQueueCount = (value: number | null | undefined): string => {
    if (value == null || Number.isNaN(Number(value))) {
      return "—";
    }
    return Number(value).toLocaleString(locale);
  };

  const formatPct = (value: number | null | undefined): string => {
    if (value == null || Number.isNaN(Number(value))) {
      return "—";
    }
    return `${Number(value).toLocaleString(locale, {
      maximumFractionDigits: 1,
    })}%`;
  };

  const formatBytesPair = (
    used: number,
    total: number,
    unit: string
  ): string =>
    `${used.toLocaleString(locale, { maximumFractionDigits: 1 })} / ${total.toLocaleString(locale, { maximumFractionDigits: 1 })} ${unit}`;

  const hostLevel = (
    pct: number | undefined,
    warn: number,
    crit: number
  ): "ok" | "warn" | "crit" => {
    if (pct == null) return "ok";
    if (pct >= crit) return "crit";
    if (pct >= warn) return "warn";
    return "ok";
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("sa.dashboard.title")}
          pageTitle={t("sa.menu.dashboard")}
        />
        <ToastContainer closeButton={false} limit={2} />

        <div className="mb-3">
          <h4 className="mb-1">{t("sa.dashboard.greeting", { name })}</h4>
          <p className="text-muted mb-0">{t("sa.dashboard.intro")}</p>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : error ? (
          <Alert color="danger" className="mb-0">
            {error}{" "}
            <button
              type="button"
              className="btn btn-link btn-sm p-0 align-baseline"
              onClick={() => load()}
            >
              {t("sa.dashboard.retry")}
            </button>
          </Alert>
        ) : !metrics ? null : (
          <>
            {!hasVolume && (
              <Alert color="info" className="mb-3">
                {t("sa.dashboard.emptyMetrics")}
              </Alert>
            )}

            {/* Alertas de proteção — logo de cara (circuit / 2ª pausa / freeze). */}
            {sesEnforcementWarn && (
              <Alert color="warning" className="mb-3 py-2">
                <i className="ri-cloud-line me-1" aria-hidden />
                {sesEnforcementWarn}
                {metrics.ses_account?.region
                  ? ` · ${metrics.ses_account.region}`
                  : ""}
              </Alert>
            )}

            {metrics.platform_freeze === true && (
              <Alert
                color="danger"
                className="mb-3 d-flex flex-wrap align-items-center justify-content-between gap-2 py-2"
              >
                <span>
                  <i className="ri-snowflake-line me-1" aria-hidden />
                  {freezeHoldHint}
                  {metrics.platform_freeze_at
                    ? ` · ${new Date(
                        metrics.platform_freeze_at
                      ).toLocaleString(locale)}`
                    : ""}
                </span>
                <Button
                  color="light"
                  size="sm"
                  disabled={clearingFreeze}
                  onClick={() => void handleClearFreeze()}
                >
                  {clearingFreeze ? (
                    <Spinner size="sm" />
                  ) : (
                    t("sa.dashboard.clearFreeze")
                  )}
                </Button>
              </Alert>
            )}

            {/* LAC-099: alerta Mercado Pago webhook secret faltando em produção. */}
            {metrics.billing?.mp_webhook_secret_alert === true && (
              <Alert color="danger" className="mb-3 py-2">
                <i className="ri-error-warning-line me-1" aria-hidden />
                {t("sa.dashboard.mpWebhookSecretMissing")}
              </Alert>
            )}

            {metrics.google_login?.client_id_alert === true && (
              <Alert color="warning" className="mb-3 py-2">
                <i className="ri-google-fill me-1" aria-hidden />
                {t("sa.dashboard.googleClientIdMissing")}
              </Alert>
            )}

            {/* Glue DNS dos NS de auth — se IP errado, tracking/DKIM quebram. */}
            {metrics.dns_glue?.ok === false && (
              <Alert color="warning" className="mb-3 py-2">
                <i className="ri-dns-line me-1" aria-hidden />
                {t("sa.dashboard.dnsGlueWarn")}
              </Alert>
            )}

            {/* LAC-139: digest de circuit — visão rápida + atalhos. */}
            {metrics.circuit_digest ? (
              <Card className="mb-3 border border-warning-subtle">
                <CardBody className="py-3">
                  <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-2">
                    <h6 className="mb-0">
                      {t("sa.dashboard.circuitDigest.title")}
                    </h6>
                    <div className="d-flex flex-wrap gap-2">
                      <Button
                        tag={Link}
                        to="/super-admin/sending?needs_sa_resume=1"
                        color="soft-danger"
                        size="sm"
                      >
                        {t("sa.dashboard.circuitDigest.ctaSending")}
                      </Button>
                      <Button
                        tag={Link}
                        to="/super-admin/reputation?circuit_open=1"
                        color="soft-warning"
                        size="sm"
                      >
                        {t("sa.dashboard.circuitDigest.ctaReputation")}
                      </Button>
                    </div>
                  </div>
                  <Row className="g-2 text-center">
                    <Col xs={6} md={3}>
                      <div className="text-muted small">
                        {t("sa.dashboard.circuitDigest.circuitsOpen")}
                      </div>
                      <div className="fs-5 fw-semibold">
                        {metrics.circuit_digest.circuits_open.toLocaleString(
                          locale
                        )}
                      </div>
                    </Col>
                    <Col xs={6} md={3}>
                      <div className="text-muted small">
                        {t("sa.dashboard.circuitDigest.awaitingSa")}
                      </div>
                      <div className="fs-5 fw-semibold">
                        {metrics.circuit_digest.awaiting_sa.toLocaleString(
                          locale
                        )}
                      </div>
                    </Col>
                    <Col xs={6} md={3}>
                      <div className="text-muted small">
                        {t("sa.dashboard.circuitDigest.autoClosed24h")}
                      </div>
                      <div className="fs-5 fw-semibold">
                        {metrics.circuit_digest.auto_closed_24h.toLocaleString(
                          locale
                        )}
                      </div>
                    </Col>
                    <Col xs={6} md={3}>
                      <div className="text-muted small">
                        {t("sa.dashboard.circuitDigest.secondPause24h")}
                      </div>
                      <div className="fs-5 fw-semibold">
                        {metrics.circuit_digest.second_pause_notified_24h.toLocaleString(
                          locale
                        )}
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            ) : null}

            {(metrics.sends_awaiting_sa_resume ?? 0) > 0 && (
              <Alert color="danger" className="mb-3 py-2">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                  <span>
                    {t("sa.dashboard.awaitingSaResumeWarn", {
                      count: metrics.sends_awaiting_sa_resume,
                    })}{" "}
                    <Link
                      to="/super-admin/sending?needs_sa_resume=1"
                      className="alert-link"
                    >
                      {t("sa.dashboard.awaitingSaResumeCta")}
                    </Link>
                  </span>
                  {(metrics.awaiting_sa_resume_send_ids?.length ?? 0) > 0 ? (
                    <Button
                      color="light"
                      size="sm"
                      onClick={() => {
                        const ids = (
                          metrics.awaiting_sa_resume_send_ids || []
                        ).join(", ");
                        void navigator.clipboard.writeText(ids).then(
                          () =>
                            toast.success(
                              t("sa.dashboard.copySendIdsSuccess", {
                                count:
                                  metrics.awaiting_sa_resume_send_ids?.length ??
                                  0,
                              }),
                              { autoClose: 2000 }
                            ),
                          () =>
                            toast.error(t("sa.dashboard.copySendIdsError"), {
                              autoClose: 3000,
                            })
                        );
                      }}
                    >
                      {t("sa.dashboard.copySendIds", {
                        ids: (metrics.awaiting_sa_resume_send_ids || []).join(
                          ", "
                        ),
                      })}
                    </Button>
                  ) : null}
                </div>
              </Alert>
            )}

            {(metrics.circuits_open_count ?? 0) > 0 && (
              <Alert color="warning" className="mb-3 py-2">
                {t("sa.dashboard.circuitsOpenWarn", {
                  count: metrics.circuits_open_count,
                })}{" "}
                <Link
                  to="/super-admin/reputation?circuit_open=1"
                  className="alert-link"
                >
                  {t("sa.dashboard.circuitsOpenCta")}
                </Link>
              </Alert>
            )}

            {metrics.tenants_at_risk > 0 && (
              <Alert color="warning" className="mb-3 py-2">
                {t("sa.dashboard.atRiskWarn", {
                  count: metrics.tenants_at_risk,
                })}{" "}
                <Link
                  to="/super-admin/reputation?at_risk=1"
                  className="alert-link"
                >
                  {t("sa.dashboard.atRiskCta")}
                </Link>
              </Alert>
            )}

            <h6 className="text-muted text-uppercase mb-2">
              {t("sa.dashboard.section.24h")}
            </h6>
            <Row className="g-3 mb-4">
              <Col xl={3} md={6}>
                <KpiCard
                  label={t("sa.dashboard.kpi.sent")}
                  value={metrics.window_24h.sent.toLocaleString(locale)}
                  hint={t("sa.dashboard.kpi.sendsCount", {
                    count: metrics.window_24h.sends_count,
                  })}
                  icon="ri-mail-send-line"
                  to="/super-admin/sending"
                />
              </Col>
              <Col xl={3} md={6}>
                <KpiCard
                  label={t("sa.dashboard.kpi.bounceRate")}
                  value={formatSaRate(metrics.window_24h.bounce_rate, locale)}
                  icon="ri-error-warning-line"
                />
              </Col>
              <Col xl={3} md={6}>
                <KpiCard
                  label={t("sa.dashboard.kpi.complaintRate")}
                  value={formatSaRate(metrics.window_24h.complaint_rate, locale)}
                  icon="ri-alarm-warning-line"
                />
              </Col>
              <Col xl={3} md={6}>
                <KpiCard
                  label={t("sa.dashboard.kpi.deliveryRate")}
                  value={formatSaRate(metrics.window_24h.delivery_rate, locale)}
                  icon="ri-checkbox-circle-line"
                />
              </Col>
            </Row>

            <h6 className="text-muted text-uppercase mb-2">
              {t("sa.dashboard.section.7d")}
            </h6>
            <Row className="g-3 mb-4">
              <Col xl={3} md={6}>
                <KpiCard
                  label={t("sa.dashboard.kpi.sent")}
                  value={metrics.window_7d.sent.toLocaleString(locale)}
                  hint={t("sa.dashboard.kpi.sendsCount", {
                    count: metrics.window_7d.sends_count,
                  })}
                  icon="ri-mail-send-line"
                />
              </Col>
              <Col xl={3} md={6}>
                <KpiCard
                  label={t("sa.dashboard.kpi.bounceRate")}
                  value={formatSaRate(metrics.window_7d.bounce_rate, locale)}
                  icon="ri-error-warning-line"
                />
              </Col>
              <Col xl={3} md={6}>
                <KpiCard
                  label={t("sa.dashboard.kpi.complaintRate")}
                  value={formatSaRate(metrics.window_7d.complaint_rate, locale)}
                  icon="ri-alarm-warning-line"
                />
              </Col>
              <Col xl={3} md={6}>
                <KpiCard
                  label={t("sa.dashboard.kpi.mrr")}
                  value={formatCents(
                    metrics.mrr_cents,
                    metrics.mrr_currency || "BRL",
                    locale
                  )}
                  icon="ri-money-dollar-circle-line"
                  to="/super-admin/plans"
                />
              </Col>
            </Row>

            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
              <h6 className="text-muted text-uppercase mb-0">
                {t("sa.dashboard.series.titleDays", { days: seriesDays })}
              </h6>
              <div
                className="btn-group btn-group-sm"
                role="group"
                aria-label={t("dashboard.series.rangeLabel")}
              >
                <button
                  type="button"
                  className={`btn btn-soft-secondary${seriesDays === 7 ? " active" : ""}`}
                  onClick={() => setSeriesDays(7)}
                >
                  {t("dashboard.series.days7")}
                </button>
                <button
                  type="button"
                  className={`btn btn-soft-secondary${seriesDays === 30 ? " active" : ""}`}
                  onClick={() => setSeriesDays(30)}
                >
                  {t("dashboard.series.days30")}
                </button>
              </div>
            </div>
            {!hasSeriesVolume ? (
              <Alert color="info" className="mb-4">
                {t("dashboard.series.empty")}
              </Alert>
            ) : (
              <Card className="mb-4">
                <CardBody>
                  <SafeApexChart
                    dir="ltr"
                    type="line"
                    height={280}
                    options={seriesChartOptions}
                    series={seriesChartData}
                    className="apex-charts"
                  />
                </CardBody>
              </Card>
            )}

            <h6 className="text-muted text-uppercase mb-2">
              {t("sa.dashboard.section.health")}
            </h6>
            <Row className="g-3 mb-3">
              <Col xl={3} md={6}>
                <KpiCard
                  label={t("sa.dashboard.kpi.tenantsTotal")}
                  value={metrics.tenants_total.toLocaleString(locale)}
                  icon="ri-building-line"
                  to="/super-admin/tenants"
                />
              </Col>
              <Col xl={3} md={6}>
                <KpiCard
                  label={t("sa.dashboard.kpi.circuitsOpen")}
                  value={(metrics.circuits_open_count ?? 0).toLocaleString(
                    locale
                  )}
                  hint={t("sa.dashboard.kpi.circuitsOpenHint")}
                  icon="ri-shut-down-line"
                  to="/super-admin/reputation?circuit_open=1"
                  tone={
                    (metrics.circuits_open_count ?? 0) > 0 ? "warn" : undefined
                  }
                />
              </Col>
              <Col xl={3} md={6}>
                <KpiCard
                  label={t("sa.dashboard.kpi.awaitingSaResume")}
                  value={(
                    metrics.sends_awaiting_sa_resume ?? 0
                  ).toLocaleString(locale)}
                  hint={t("sa.dashboard.kpi.awaitingSaResumeHint")}
                  icon="ri-pause-circle-line"
                  to="/super-admin/sending?needs_sa_resume=1"
                  tone={
                    (metrics.sends_awaiting_sa_resume ?? 0) > 0
                      ? "crit"
                      : undefined
                  }
                />
              </Col>
              <Col xl={3} md={6}>
                <KpiCard
                  label={t("sa.dashboard.kpi.tenantsAtRisk")}
                  value={metrics.tenants_at_risk.toLocaleString(locale)}
                  icon="ri-heart-pulse-line"
                  to="/super-admin/reputation?at_risk=1"
                  tone={metrics.tenants_at_risk > 0 ? "warn" : undefined}
                />
              </Col>
            </Row>
            <Row className="g-3 mb-3">
              <Col xl={3} md={6}>
                <KpiCard
                  label={t("sa.dashboard.kpi.tenantsBlocked")}
                  value={metrics.tenants_blocked.toLocaleString(locale)}
                  icon="ri-forbid-line"
                  to="/super-admin/tenants?blocked=1"
                />
              </Col>
              <Col xl={3} md={6}>
                <KpiCard
                  label={t("sa.dashboard.kpi.suppressions")}
                  value={(
                    metrics.email_suppressions_count +
                    metrics.domain_suppressions_count
                  ).toLocaleString(locale)}
                  hint={t("sa.dashboard.kpi.suppressionsHint", {
                    emails: metrics.email_suppressions_count,
                    domains: metrics.domain_suppressions_count,
                  })}
                  icon="ri-shield-keyhole-line"
                  to="/super-admin/email-suppressions"
                />
              </Col>
            </Row>

            {metrics.queue ? (
              <>
                <h6 className="text-muted text-uppercase mb-2">
                  {t("sa.dashboard.queueTitle")}
                </h6>
                <Row className="g-3 mb-3">
                  <Col md={6} xl={3}>
                    <KpiCard
                      label={t("sa.dashboard.sendPending")}
                      value={formatQueueCount(metrics.queue.send_pending)}
                      icon="ri-stack-line"
                      to="/super-admin/sending"
                    />
                  </Col>
                  {metrics.queue.send_p0_pending != null ? (
                    <Col md={6} xl={3}>
                      <KpiCard
                        label={t("sa.dashboard.sendP0Pending")}
                        value={formatQueueCount(metrics.queue.send_p0_pending)}
                        hint={t("sa.dashboard.sendP0PendingHint")}
                        icon="ri-flashlight-line"
                        to="/super-admin/sending"
                      />
                    </Col>
                  ) : null}
                  {metrics.queue.import_pending != null ? (
                    <Col md={6} xl={3}>
                      <KpiCard
                        label={t("sa.dashboard.importPending")}
                        value={formatQueueCount(metrics.queue.import_pending)}
                        icon="ri-upload-cloud-2-line"
                      />
                    </Col>
                  ) : null}
                  <Col md={6} xl={3}>
                    <KpiCard
                      label={t("sa.dashboard.workerStatus")}
                      value={
                        metrics.queue.worker_ok === true
                          ? t("sa.dashboard.workerOk")
                          : metrics.queue.worker_ok === false
                            ? t("sa.dashboard.workerDown")
                            : t("sa.dashboard.workerUnknown")
                      }
                      tone={
                        metrics.queue.worker_ok === false
                          ? "crit"
                          : metrics.queue.worker_ok === true
                            ? "ok"
                            : undefined
                      }
                      icon="ri-cpu-line"
                    />
                  </Col>
                  <Col md={6} xl={3}>
                    <KpiCard
                      label={t("sa.dashboard.failedJobs")}
                      value={formatQueueCount(metrics.queue.failed_jobs)}
                      icon="ri-close-circle-line"
                    />
                  </Col>
                </Row>
              </>
            ) : null}

            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
              <h6 className="text-muted text-uppercase mb-0">
                {t("sa.dashboard.hostTitle")}
              </h6>
              <Button
                color="soft-secondary"
                size="sm"
                disabled={requestingHostSample}
                onClick={() => void handleRequestHostSample()}
              >
                {requestingHostSample ? (
                  <Spinner size="sm" />
                ) : (
                  t("sa.dashboard.host.requestSample")
                )}
              </Button>
            </div>
            {!hostMetrics || !hostMetrics.available || !hostMetrics.current ? (
              <Alert color="secondary" className="mb-3 py-2">
                {t("sa.dashboard.hostUnavailable")}
                {hostMetrics?.sample_requested ? (
                  <span className="d-block small mt-1">
                    {t("sa.dashboard.host.sampleRequestedPending")}
                  </span>
                ) : null}
              </Alert>
            ) : (
              <>
                {(() => {
                  const cpuTone = hostLevel(
                    hostMetrics.current.cpu_pct,
                    hostMetrics.thresholds.cpu_pct_warn,
                    hostMetrics.thresholds.cpu_pct_crit
                  );
                  const ramTone = hostLevel(
                    hostMetrics.current.ram_used_pct,
                    hostMetrics.thresholds.ram_pct_warn,
                    hostMetrics.thresholds.ram_pct_crit
                  );
                  const diskTone = hostLevel(
                    hostMetrics.current.disk_used_pct,
                    hostMetrics.thresholds.disk_pct_warn,
                    hostMetrics.thresholds.disk_pct_crit
                  );
                  const worst: KpiTone =
                    cpuTone === "crit" ||
                    ramTone === "crit" ||
                    diskTone === "crit"
                      ? "crit"
                      : cpuTone === "warn" ||
                          ramTone === "warn" ||
                          diskTone === "warn"
                        ? "warn"
                        : "ok";
                  const cpuSeries = (hostMetrics.series || []).map(
                    (p) => p.cpu_pct
                  );

                  return (
                    <>
                      {hostMetrics.stale ? (
                        <Alert color="warning" className="mb-2 py-2">
                          {t("sa.dashboard.hostStale")}
                        </Alert>
                      ) : null}
                      {hostMetrics.sample_requested ? (
                        <Alert color="info" className="mb-2 py-2">
                          {t("sa.dashboard.host.sampleRequestedPending")}
                        </Alert>
                      ) : null}
                      {worst === "crit" ? (
                        <Alert color="danger" className="mb-2 py-2">
                          {t("sa.dashboard.hostCritAlert")}
                        </Alert>
                      ) : worst === "warn" ? (
                        <Alert color="warning" className="mb-2 py-2">
                          {t("sa.dashboard.hostWarnAlert")}
                        </Alert>
                      ) : null}
                      <Row className="g-3 mb-2">
                        <Col xl={4} md={6}>
                          <KpiCard
                            label={t("sa.dashboard.host.cpuNow")}
                            value={formatPct(hostMetrics.current.cpu_pct)}
                            hint={t("sa.dashboard.host.peakHint", {
                              peak: formatPct(hostMetrics.peak_24h?.cpu_pct),
                            })}
                            icon="ri-cpu-line"
                            tone={cpuTone}
                          />
                        </Col>
                        <Col xl={4} md={6}>
                          <KpiCard
                            label={t("sa.dashboard.host.ramNow")}
                            value={formatPct(hostMetrics.current.ram_used_pct)}
                            hint={formatBytesPair(
                              hostMetrics.current.ram_used_mb,
                              hostMetrics.current.ram_total_mb,
                              "MB"
                            )}
                            icon="ri-database-2-line"
                            tone={ramTone}
                          />
                        </Col>
                        <Col xl={4} md={6}>
                          <KpiCard
                            label={t("sa.dashboard.host.diskNow")}
                            value={formatPct(hostMetrics.current.disk_used_pct)}
                            hint={formatBytesPair(
                              hostMetrics.current.disk_used_gb,
                              hostMetrics.current.disk_total_gb,
                              "GB"
                            )}
                            icon="ri-hard-drive-2-line"
                            tone={diskTone}
                          />
                        </Col>
                      </Row>
                      {cpuSeries.length >= 2 ? (
                        <div className="mb-2 text-primary">
                          <span className="text-muted small d-block mb-1">
                            {t("sa.dashboard.host.cpuSparkline")}
                          </span>
                          <CpuSparkline values={cpuSeries} />
                        </div>
                      ) : null}
                      <p className="text-muted small mb-1">
                        {t("sa.dashboard.host.sampledAt", {
                          at: new Date(
                            hostMetrics.current.sampled_at
                          ).toLocaleString(locale),
                        })}
                      </p>
                      <p className="text-muted small mb-3">
                        {t("sa.dashboard.host.peakSummary", {
                          cpu: formatPct(hostMetrics.peak_24h?.cpu_pct),
                          ram: formatPct(hostMetrics.peak_24h?.ram_used_pct),
                          disk: formatPct(hostMetrics.peak_24h?.disk_used_pct),
                          samples: hostMetrics.sample_count_24h,
                        })}
                        {hostMetrics.netdata_url ? (
                          <>
                            {" · "}
                            <a
                              href={hostMetrics.netdata_url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {t("sa.dashboard.host.openNetdata")}
                            </a>
                          </>
                        ) : null}
                      </p>
                    </>
                  );
                })()}
              </>
            )}

            {metrics.platform_freeze === null ? (
              <p className="text-muted small mb-0">
                {t("sa.dashboard.freezeUnavailable")}
              </p>
            ) : metrics.platform_freeze ? null : (
              <p className="text-muted small mb-0 d-inline-flex align-items-center gap-1">
                <i className="ri-checkbox-circle-line text-success" aria-hidden />
                {t("sa.dashboard.freezeOk")}
              </p>
            )}
          </>
        )}
      </Container>
    </div>
  );
};

export default SuperAdminDashboard;
